import { Modal, Button } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";

/** BillingType: 0 = Invoice, 1 = Transaction (see billingTypeOptions). */
export const BILLING_TYPE_INVOICE = 0;
export const BILLING_TYPE_TRANSACTION = 1;

/** LicenseType / period: 0 = Monthly, 1 = Lifetime (see orderTypeOptions). */
export const LICENSE_PERIOD_MONTHLY = 0;
export const LICENSE_PERIOD_LIFETIME = 1;
export const LICENSE_PERIOD_YEARLY = 2;

/**
 * Compute Valid till / Renewal at for site & device billing flows.
 * - Lifetime: neither
 * - Transaction + monthly (or yearly): Valid till only
 * - Invoice + monthly (or yearly): Valid till + Renewal at
 */
function normalizeBillingType(billingType) {
  if (billingType == null || billingType === "") return null;
  const n = Number(billingType);
  if (Number.isFinite(n)) return n;
  const s = String(billingType).toLowerCase();
  if (s.includes("transaction")) return BILLING_TYPE_TRANSACTION;
  if (s.includes("invoice")) return BILLING_TYPE_INVOICE;
  return null;
}

function normalizeLicensePeriod(licensePeriod) {
  if (licensePeriod == null || licensePeriod === "") return null;
  const n = Number(licensePeriod);
  if (Number.isFinite(n)) return n;
  const s = String(licensePeriod).toLowerCase();
  if (s.includes("life")) return LICENSE_PERIOD_LIFETIME;
  if (s.includes("year")) return LICENSE_PERIOD_YEARLY;
  if (s.includes("month")) return LICENSE_PERIOD_MONTHLY;
  return null;
}

export function computeValidityDates({
  showValidityDates,
  billingType,
  licensePeriod,
  paidAt,
}) {
  if (!showValidityDates) {
    return { validTill: null, renewalAt: null };
  }

  const period = normalizeLicensePeriod(licensePeriod);
  if (period === LICENSE_PERIOD_LIFETIME) {
    return { validTill: null, renewalAt: null };
  }

  const base = paidAt instanceof Date ? paidAt : new Date(paidAt);
  if (Number.isNaN(base.getTime())) {
    return { validTill: null, renewalAt: null };
  }

  const end = new Date(base.getTime());
  if (period === LICENSE_PERIOD_YEARLY) {
    end.setFullYear(end.getFullYear() + 1);
  } else {
    // Monthly (0), null/unknown → +1 month
    end.setMonth(end.getMonth() + 1);
  }

  const bt = normalizeBillingType(billingType);
  if (bt === BILLING_TYPE_INVOICE) {
    return { validTill: end, renewalAt: end };
  }
  // Transaction (1) or unknown billing → Valid till only when period-based
  return { validTill: end, renewalAt: null };
}

export function formatPaidAt(date) {
  if (!date) return "—";
  const m = moment(date);
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : "—";
}

export function formatValidDate(date) {
  if (!date) return "—";
  const m = moment(date);
  return m.isValid() ? m.format("MMM D, YYYY") : "—";
}

export function formatAmountDisplay(amount) {
  if (amount == null || amount === "") return "—";
  if (typeof amount === "string" && amount.trim().startsWith("$")) {
    return amount.trim();
  }
  const num = Number(String(amount).replace(/[^0-9.-]+/g, ""));
  if (!Number.isFinite(num)) return String(amount);
  return `$${num.toFixed(2)}`;
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-[13px] text-[#8c8c8c] font-[Poppins] shrink-0">
        {label}
      </span>
      <span className="text-[13px] font-semibold text-black font-[Poppins] text-right break-all">
        {value}
      </span>
    </div>
  );
}

/**
 * Payment success confirmation — shown after shared PaymentForm succeeds.
 */
const PaymentSuccessModal = ({
  open,
  onClose,
  dealerEmail,
  paymentNo,
  amount,
  method,
  paidAt,
  validTill,
  renewalAt,
}) => {
  const showValidTill = validTill != null;
  const showRenewalAt = renewalAt != null;

  const emailSubtitle = dealerEmail
    ? `Receipt sent to ${dealerEmail}`
    : "Receipt will be sent by email";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={400}
      destroyOnClose
      maskClosable={false}
      zIndex={1200}
      styles={{
        body: { padding: "28px 28px 24px" },
      }}
    >
      <div className="relative font-[Poppins]">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute -top-1 -right-1 flex items-center justify-center w-8 h-8 text-[#8c8c8c] hover:text-black bg-transparent border-0 cursor-pointer"
        >
          <CloseOutlined className="text-[12px]" />
        </button>

        <div className="flex flex-col items-center pt-1">
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#52c41a] text-white mb-4">
            <CheckOutlined className="text-[28px]" />
          </span>
          <h2 className="m-0 text-[20px] font-semibold text-black leading-tight text-center">
            Payment Successful
          </h2>
          <p className="m-0 mt-2 text-[13px] text-[#8c8c8c] text-center">
            {emailSubtitle}
          </p>
        </div>

        <div className="mt-5 mb-5">
          <DetailRow
            label="Payment No"
            value={
              paymentNo != null && paymentNo !== "" ? String(paymentNo) : "—"
            }
          />
          <DetailRow label="Amount" value={formatAmountDisplay(amount)} />
          <DetailRow label="Method" value={method || "—"} />
          <DetailRow label="Paid at" value={formatPaidAt(paidAt)} />
          {showValidTill ? (
            <DetailRow label="Valid till" value={formatValidDate(validTill)} />
          ) : null}
          {showRenewalAt ? (
            <DetailRow label="Renewal at" value={formatValidDate(renewalAt)} />
          ) : null}
        </div>

        <div className="flex justify-center">
          <Button
            type="primary"
            onClick={onClose}
            className="mr-2 w-[104px] h-[30px] font-medium !bg-[var(--text-color)] !border-[var(--text-color)] hover:!bg-[var(--text-color)] hover:!text-white"
          >
            <IoIosCheckmarkCircleOutline className="!text-[12px] font-medium" />
            Ok
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSuccessModal;
